import type React from "react";
import { useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { A } from "@/atoms-react/a/A.react";
import { Alert, AlertType } from "@/atoms-react/alert/index.react";
import { Button } from "@/atoms-react/button/Button.react";
import Column from "@/atoms-react/column/Column.react";
import Loader from "@/atoms-react/loader/Loader.react";
import { Modal } from "@/atoms-react/modal/index.react";
import { RadioCard } from "@/atoms-react/radio-card/index.react";
import { Base, Info } from "@/atoms-react/text/Text.react";
import { EmptyPageCactusAnimation } from "@/components/animations/empty-cactus/index.react";
import { getJwt } from "@/features/auth/utils";
import { useCompany } from "@/features/company/hooks/use-company";
import { useCompanyContext } from "@/features/company/hooks/use-company-context";
import { MandateService } from "@/features/company/types";
import { Routes } from "@/features/router";
import { useAliasMail } from "@/features/user/hooks/use-alias-mail";
import { useAliasMobile } from "@/features/user/hooks/use-alias-mobile";
import { useSetAliasPublic } from "@/features/user/hooks/use-set-alias-public";
import { useUser } from "@/features/user/hooks/use-user";
import { useUserCheckAccess } from "@/features/user/hooks/use-user-check-access";
import { type AliasIBAN, type AliasMobile, PublicAliasType } from "@/features/user/types";
import { NaturalEntityDataType } from "@/features/utils/types";
import { EmailCard } from "@/molecules/email-card/index.react";
import { IbanCard } from "@/molecules/iban-card/index.react";
import { PhoneCard } from "@/molecules/phone-card/index.react";
import { useAppDispatch } from "@/redux/hooks";
import {
  OlkyPassProTab,
  setOlkypassPassProTab,
  setOlkypassPassProTabAndAddAlias
} from "@/redux/slices/companyOnboardingSlice";
import { Themes } from "@/types/theme";
import { AliasMailSchema } from "@/types/user/alias-mail.types";
import { AliasMobileSchema } from "@/types/user/alias-mobile.types";
import { LayoutTitle } from "../../../_layout/index.react";
import { PublicVisibilityTitleSection } from "./title-section";

type AliasesPublicAppProps = { isOnboarding?: boolean } & React.ComponentPropsWithoutRef<"div">;

export const AliasesPublicApp = ({ isOnboarding = false, ...props }: AliasesPublicAppProps) => {
  const { t } = useTranslation(["pass"]);
  const { useUserProfile } = useUser();
  const navigate = useNavigate();
  const { data: user, refetch: refetchUserProfile } = useUserProfile();
  const { companyDetailQuery } = useCompany();
  const { checkPermission } = useUserCheckAccess();
  const { leUuid } = useCompanyContext();
  const dispatch = useAppDispatch();
  const { useGetAliasMail, useSetPublicAliasMail } = useAliasMail();
  const { data: emails = [], isLoading: emailsLoading } = useGetAliasMail({
    enabled: !!getJwt()
  });
  const { mutateAsync: setPublicAliasMail } = useSetPublicAliasMail();
  const { useGetAliasMobile, useSetPublicAliasMobile } = useAliasMobile();
  const { data: phones = [], isLoading: phonesLoading } = useGetAliasMobile({
    enabled: !!getJwt()
  });
  const { mutateAsync: setPublicAliasMobile } = useSetPublicAliasMobile();
  const { data: companyDetails, isLoading: isCompanyDetailsLoading } = companyDetailQuery(
    leUuid as string
  );
  const ibans = (() =>
    leUuid
      ? (companyDetails?.legalEntity.AliasIBAN ?? [])
      : (user?.naturalPerson?.AliasIBAN ?? []))();

  const email = (() => emails.find((email) => email.public) ?? null)();

  const phone = (() => phones.find((phone) => phone.public) ?? null)();
  const iban = (() => ibans.find((iban) => iban.Public) ?? null)();

  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalType, setModalType] = useState<PublicAliasType | null>();
  const enabled = (() =>
    !leUuid
      ? true
      : !companyDetails?.legalEntity.UserValidation &&
        checkPermission(MandateService.ALIASES_MANAGEMENT, user, companyDetails))();
  const [selected, setSelected] = useState<AliasMobileSchema | AliasIBAN | AliasMailSchema | null>(
    null
  );

  const sectionDivClassName = "rounded-sm md:px-6 md:py-4";

  const { setAliasPublic } = useSetAliasPublic();
  const onModalConfirm = () => {
    let list: AliasMobile[] | AliasIBAN[] | AliasMailSchema[];
    if (leUuid) {
      if (!companyDetails || !companyDetails.legalEntity) {
        return;
      }
      list = emails;
      if (modalType === PublicAliasType.Phone) {
        list = companyDetails.legalEntity.AliasMobile;
      } else if (modalType === PublicAliasType.Iban) list = companyDetails.legalEntity.AliasIBAN;
    } else {
      if (!user || !user.naturalPerson) {
        return;
      }
      list = emails;
      if (modalType === PublicAliasType.Phone) {
        list = user.naturalPerson.AliasMobile;
      } else if (modalType === PublicAliasType.Iban) list = user.naturalPerson.AliasIBAN;
    }
    if (
      modalType === PublicAliasType.Iban &&
      selected &&
      (selected as AliasIBAN)?.valid === false
    ) {
      toast.error(t("olkypass.aliasPublicPage.validate_iban_first"));
      return;
    }

    switch (modalType) {
      case PublicAliasType.Email:
        updateMailPublicVisibility(selected as AliasMailSchema);
        break;
      case PublicAliasType.Phone:
        updateMobilePublicVisibility(selected as AliasMobileSchema);
        break;
      case PublicAliasType.Iban:
        updateIbanPublicVisibility(selected as AliasIBAN, list as AliasIBAN[]);
        break;
    }

    setModalOpen(false);
  };

  const updateMailPublicVisibility = (mail: AliasMailSchema) => {
    if (mail) {
      setPublicAliasMail({
        aliasMailUuid: mail.uuid,
        public: true
      });
    } else if (email) {
      setPublicAliasMail({
        aliasMailUuid: email.uuid,
        public: false
      });
    }
  };

  const updateMobilePublicVisibility = (mobile: AliasMobileSchema) => {
    if (mobile) {
      setPublicAliasMobile({
        aliasMobileUuid: mobile.uuid,
        public: true
      });
    } else if (phone) {
      setPublicAliasMobile({
        aliasMobileUuid: phone.uuid,
        public: false
      });
    }
  };

  const updateIbanPublicVisibility = async (alias: AliasIBAN | null, aliases: AliasIBAN[]) => {
    const type = NaturalEntityDataType.AliasIBANDto;

    const publicAliases = aliases!.filter((alias) => alias.Public);

    const updateToDo = publicAliases.map((pa) =>
      setAliasPublic(type, pa.UUID, false, user, companyDetails)
    );

    if (alias) {
      updateToDo.push(setAliasPublic(type, alias.UUID, true, user, companyDetails));
    }

    await Promise.all(updateToDo);

    await refetchUserProfile();
  };

  const openModal = (type: PublicAliasType) => {
    let selection: AliasMobileSchema | AliasIBAN | AliasMailSchema | null = email ?? null;
    if (type === PublicAliasType.Phone) {
      selection = phone ?? null;
    } else if (type === PublicAliasType.Iban) {
      selection = iban ?? null;
    }
    setSelected(selection);
    setModalOpen(true);
    setModalType(type);
  };

  return (
    <Column {...props}>
      {!isOnboarding && (
        <LayoutTitle>
          {t("olkypass.mainPage.navigation_account_menu.link.alias_public")}
        </LayoutTitle>
      )}

      <div className="flex w-full flex-col gap-y-10 md:gap-y-6">
        {/* ID and QR code section */}
        <div
          className={twMerge(
            "flex flex-col-reverse justify-between bg-[url('/images/olkypass/olky-space-background.jpg')] bg-center bg-cover bg-no-repeat p-6 md:flex-row md:items-center",
            sectionDivClassName
          )}
        >
          <PublicVisibilityTitleSection
            user={user}
            companyDetails={companyDetails}
            isCompanyDetailsLoading={isCompanyDetailsLoading}
          />
          {/* QR Code : just a square now  */}
          <div className="invisible mb-5 h-24 w-24 rounded-sm bg-white md:my-2"></div>
        </div>

        {/* Email section */}
        <div
          className={twMerge(
            "md:border-2 md:border-slate-50 dark:md:border-slate-800",
            sectionDivClassName
          )}
          data-cy="public-email-section"
        >
          <div className="mb-2 flex items-center justify-between">
            <Base className="text-slate-300">E-mail</Base>
            {emails && emails.length > 0 && (
              <A
                dataCy={"email-change-public-visibility-btn"}
                disabled={!enabled}
                onClick={() => openModal(PublicAliasType.Email)}
              >
                {t("olkypass.aliasPublicPage.change")}
              </A>
            )}
            {emails && emails.length === 0 && (
              <A
                disabled={!enabled}
                onClick={() =>
                  isOnboarding
                    ? dispatch(setOlkypassPassProTab(OlkyPassProTab.Emails))
                    : navigate({
                        pathname: `${Routes.account_alias_email}${leUuid ? `/${leUuid}` : ""}`
                      })
                }
              >
                {t("aliasEmailPage.button.add_email")}
              </A>
            )}
          </div>
          <div>
            {!email && (
              <>
                {emailsLoading ? (
                  <div className="my-4 flex w-full flex-col items-center">
                    <Loader />
                  </div>
                ) : (
                  <div className="flex w-full flex-col items-center space-y-4">
                    <EmptyPageCactusAnimation />
                    <Info>{t("olkypass.aliasPublicPage.no_email")}</Info>
                  </div>
                )}
              </>
            )}
            {email && <EmailCard email={email} dataCy={"pv-email-card"} />}
          </div>
        </div>

        {/* Phone section */}
        <div
          className={twMerge(
            "md:border-2 md:border-slate-50 dark:md:border-slate-800",
            sectionDivClassName
          )}
          data-cy="public-mobile-section"
        >
          <div className="flex items-center justify-between">
            <Base className="text-slate-300">{t("olkypass.aliasPublicPage.phone_number")}</Base>
            {phones.length > 0 && (
              <A
                disabled={!enabled}
                onClick={() => openModal(PublicAliasType.Phone)}
                dataCy={"mobile-change-public-visibility-btn"}
              >
                {t("olkypass.aliasPublicPage.change")}
              </A>
            )}
            {phones.length === 0 && (
              <A
                disabled={!enabled}
                onClick={() =>
                  isOnboarding
                    ? dispatch(setOlkypassPassProTabAndAddAlias(OlkyPassProTab.Mobiles))
                    : navigate({
                        pathname: `${Routes.account_alias_mobile}${leUuid ? `/${leUuid}` : ""}`,
                        search: "?add=1"
                      })
                }
              >
                {t("aliasMobilePage.button.add_mobile")}
              </A>
            )}
          </div>
          <div>
            {!phone && (
              <>
                {phonesLoading ? (
                  <div className="my-4 flex w-full flex-col items-center">
                    <Loader />
                  </div>
                ) : (
                  <div className="flex w-full flex-col items-center space-y-4">
                    <EmptyPageCactusAnimation />
                    <Info>{t("olkypass.aliasPublicPage.no_phone_number")}</Info>
                  </div>
                )}
              </>
            )}
            {phone && <PhoneCard phone={phone} dataCy={"pv-mobile-card"} />}
          </div>
        </div>

        {/* Iban section */}
        <div
          className={twMerge(
            "md:border-2 md:border-slate-50 dark:md:border-slate-800",
            sectionDivClassName
          )}
          data-cy="public-iban-section"
        >
          <div className="flex items-center justify-between">
            <Base className="text-slate-300">IBAN</Base>
            {ibans.length > 0 && (
              <A
                disabled={!enabled}
                onClick={() => openModal(PublicAliasType.Iban)}
                dataCy={"iban-change-public-visibility-btn"}
              >
                {t("olkypass.aliasPublicPage.change")}
              </A>
            )}
            {ibans.length === 0 && (
              <A
                disabled={!enabled}
                onClick={() =>
                  isOnboarding
                    ? dispatch(setOlkypassPassProTabAndAddAlias(OlkyPassProTab.Ibans))
                    : navigate({
                        pathname: `${Routes.account_alias_payments}${leUuid ? `/${leUuid}` : ""}`,
                        search: "?add=1"
                      })
                }
              >
                {t("aliasPaymentsPage.button.add_iban")}
              </A>
            )}
          </div>
          <div>
            {!iban && (
              <>
                <div className="flex w-full flex-col items-center space-y-4">
                  <EmptyPageCactusAnimation />
                  <Info>{t("olkypass.aliasPublicPage.no_iban")}</Info>
                </div>
              </>
            )}
            {iban && <IbanCard iban={iban} dataCy={"pv-iban-card"} />}
          </div>
        </div>
      </div>
      <Modal maxWidthClassName="max-w-2xl" open={modalOpen} onClose={() => setModalOpen(false)}>
        <div className="flex flex-col gap-4">
          <Alert
            type={AlertType.Info}
            title={
              modalType
                ? {
                    [PublicAliasType.Email]: t("olkypass.aliasPublicPage.choose_new_public_email"),
                    [PublicAliasType.Phone]: t("olkypass.aliasPublicPage.choose_new_public_phone"),
                    [PublicAliasType.Iban]: t("olkypass.aliasPublicPage.choose_new_public_iban")
                  }[modalType]
                : ""
            }
            className="mt-4"
          />
          <div className="flex max-h-96 flex-col gap-1 overflow-y-auto">
            <RadioCard
              dataCy={"pv-radio-card"}
              onClick={() => setSelected(null)}
              active={selected === null}
            >
              <Base>{t("olkypass.aliasPublicPage.None")}</Base>
            </RadioCard>
            {modalType === PublicAliasType.Email &&
              emails &&
              emails.map((aliasEmail) => (
                <RadioCard
                  dataCy={"pv-radio-card"}
                  key={aliasEmail.uuid}
                  className="items-center"
                  active={selected === aliasEmail}
                  onClick={() => setSelected(aliasEmail)}
                >
                  <EmailCard email={aliasEmail} />
                </RadioCard>
              ))}
            {modalType === PublicAliasType.Phone &&
              phones &&
              phones.map((aliasPhone) => (
                <RadioCard
                  dataCy={"pv-radio-card"}
                  key={aliasPhone.uuid}
                  className="items-center"
                  active={selected === aliasPhone}
                  onClick={() => setSelected(aliasPhone)}
                >
                  <PhoneCard phone={aliasPhone} />
                </RadioCard>
              ))}
            {modalType === PublicAliasType.Iban &&
              ibans &&
              ibans.map((aliasIban) => (
                <RadioCard
                  dataCy={"pv-radio-card"}
                  key={aliasIban.UUID}
                  className="items-center"
                  active={selected === aliasIban}
                  onClick={() => {
                    setSelected(aliasIban);
                  }}
                >
                  <IbanCard iban={aliasIban} />
                </RadioCard>
              ))}
          </div>
          <div className="flex justify-end gap-4">
            <Button onClick={() => setModalOpen(false)} theme={Themes.white} className="w-max">
              {t("olkypass.aliasPublicPage.cancel")}
            </Button>
            <Button
              dataCy={"btn-confirm-pv"}
              disabled={
                modalType === PublicAliasType.Iban
                  ? selected === iban
                  : modalType === PublicAliasType.Phone
                    ? selected === phone
                    : selected === email
              }
              onClick={onModalConfirm}
              className="w-max"
            >
              {t("olkypass.aliasPublicPage.update")}
            </Button>
          </div>
        </div>
      </Modal>
    </Column>
  );
};
